import React, { createContext, useContext, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import {
  SegmentationBlocksContextType,
  ISegmentationBlock,
  SegmentationBlockFeatureType,
} from "../@types/segmentationBlocks";
import { AuthContext } from "../../../providers/AuthProvider";
import {
  useSegmentationBlocksListApiService,
  useCreateSegmentationBlockApiService,
  useUpdateSegmentationBlockApiService,
} from "../services/api/SegmentationBlocksApiService";

// Stable empty array to prevent unnecessary re-renders
const STABLE_EMPTY_ARRAY: ISegmentationBlock[] = [];

export const SegmentationBlocksContext =
  createContext<SegmentationBlocksContextType | null>(null);

export const SegmentationBlocksProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const auth = useContext(AuthContext);

  // Fetch segmentation blocks
  const {
    data: blocksData,
    isLoading,
    isError,
    refetch,
  } = useSegmentationBlocksListApiService();

  // Create mutation
  const { mutate: createMutation, isLoading: isCreating } =
    useCreateSegmentationBlockApiService();

  // Update mutation
  const { mutate: updateMutation, isLoading: isUpdating } =
    useUpdateSegmentationBlockApiService();

  const segmentationBlocks = useMemo(
    () => blocksData?.results ?? STABLE_EMPTY_ARRAY,
    [blocksData?.results]
  );

  // Filter active and inactive blocks
  const activeBlocks = useMemo(
    () =>
      segmentationBlocks.filter((block: ISegmentationBlock) => block.is_active),
    [segmentationBlocks]
  );

  const inactiveBlocks = useMemo(
    () =>
      segmentationBlocks.filter(
        (block: ISegmentationBlock) => !block.is_active
      ),
    [segmentationBlocks]
  );

  // Create block action
  const createBlock = useCallback(
    (
      segmentIds: number[],
      title: string,
      reason: string,
      isActive: boolean
    ): Promise<void> =>
      new Promise((resolve, reject) => {
        createMutation(
          {
            segments: segmentIds,
            feature_type: SegmentationBlockFeatureType.EWA_BLOCKING,
            config: { title, reason },
            is_active: isActive,
          },
          {
            onSuccess: () => {
              showNotification({
                title: "Segmentation Block Created",
                message: "The segment has been successfully blocked from EWA.",
                color: "green",
                autoClose: 4000,
              });
              resolve();
            },
            onError: () => {
              showNotification({
                title: "Error Creating Block",
                message:
                  "Failed to create segmentation block. Please try again.",
                color: "red",
                autoClose: 4000,
              });
              reject(new Error("Failed to create segmentation block"));
            },
          }
        );
      }),
    [createMutation]
  );

  // Update block action (toggle active/inactive)
  const updateBlock = useCallback(
    (id: number, isActive: boolean): Promise<void> =>
      new Promise((resolve, reject) => {
        updateMutation(
          { id, is_active: isActive },
          {
            onSuccess: () => {
              showNotification({
                title: "Block Updated",
                message: `The segment block has been ${
                  isActive ? "activated" : "deactivated"
                }.`,
                color: "green",
                autoClose: 4000,
              });
              resolve();
            },
            onError: () => {
              showNotification({
                title: "Error Updating Block",
                message:
                  "Failed to update segmentation block. Please try again.",
                color: "red",
                autoClose: 4000,
              });
              reject(new Error("Failed to update segmentation block"));
            },
          }
        );
      }),
    [updateMutation]
  );

  const value: SegmentationBlocksContextType = useMemo(
    () => ({
      segmentationBlocks,
      activeBlocks,
      inactiveBlocks,
      isLoading,
      isError,
      createBlock,
      updateBlock,
      refetch,
      isCreating,
      isUpdating,
    }),
    [
      segmentationBlocks,
      activeBlocks,
      inactiveBlocks,
      isLoading,
      isError,
      createBlock,
      updateBlock,
      refetch,
      isCreating,
      isUpdating,
    ]
  );

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <SegmentationBlocksContext.Provider value={value}>
      {children}
    </SegmentationBlocksContext.Provider>
  );
};
